import { memo } from 'react';
import styled from '@emotion/styled';
import { tokens, BasePopover, Text } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';

const ScoreContainer = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: ${tokens.spacer.x0pt5} ${tokens.spacer.x1};
  border-radius: ${tokens.spacer.x0pt5};
  transition: all 240ms ease-in-out 0s;

  &:hover {
    background-color: ${tokens.color.bg_hover};
  }
`;

const ScoreVisualization = styled.ul`
  margin: 0 ${tokens.spacer.x0pt5} 0 0;
  padding: 0;
  list-style-type: none;
`;

const ScoreBar = styled.li<{ isActive: boolean; colorActive: string; colorInactive: string }>`
  display: inline-block;
  width: ${tokens.spacer.x0pt5};
  height: ${tokens.spacer.x1pt5};

  margin-right: ${tokens.spacer.x0pt5};
  border-radius: ${tokens.spacer.x0pt5};

  background-color: ${({ isActive, colorActive, colorInactive }) =>
    isActive ? colorActive : colorInactive};
`;

type ScoreParams = {
  score: number;
  colorActive: string;
  colorInactive: string;
  maxValue?: number;
  showLabel?: boolean;
};

export const Score = ({
  score,
  colorActive,
  colorInactive,
  maxValue = 3,
  showLabel = true,
}: ScoreParams): JSX.Element => (
  <>
    <ScoreVisualization aria-hidden="true">
      {[...Array(maxValue)].map((_, i) => (
        <ScoreBar
          // This is a static component, so it's safe to use index as a key prop
          key={`score-bar-${i}`}
          isActive={i < score}
          colorActive={colorActive}
          colorInactive={colorInactive}
          data-testid={`score-bar-${i < score ? 'active' : 'inactive'}`}
        />
      ))}
    </ScoreVisualization>
    {showLabel && score}
  </>
);

const PopoverContent = styled(Text)`
  display: block;
  margin-top: -${tokens.spacer.x1pt5};
`;

const PopoverScoreContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${tokens.spacer.x1};
`;

type ScoreCellParams = {
  score: number;
  colorActive: string;
  colorInactive: string;
  text: string;
  title: string;
  trackingLabel: string;
  maxValue?: number;
  trigger?: 'click' | 'mouseenter focus';
};

const UnMemoScoreCell = ({
  score,
  colorActive,
  colorInactive,
  text,
  title,
  trackingLabel,
  maxValue = 3,
  trigger = 'click',
}: ScoreCellParams): JSX.Element => {
  const { data: currentSite } = useCurrentSite();
  const { data: user } = useCurrentUser();
  const { click } = useTrack();

  const scoreInstance = (
    <Score
      score={score}
      colorActive={colorActive}
      colorInactive={colorInactive}
      maxValue={maxValue}
    />
  );

  return (
    <BasePopover
      placement="bottom-end"
      trigger={trigger}
      withCloseButton={trigger === 'click' ? true : false}
      content={
        <PopoverContent>
          <PopoverScoreContainer>{scoreInstance}</PopoverScoreContainer>
          <Text as="p" textType="subtitle2">
            {title}
          </Text>
          {text}
        </PopoverContent>
      }
    >
      <ScoreContainer
        onClick={() => {
          click(`View ${trackingLabel} Score Tooltip`, `${trackingLabel} Score Column`, {
            account_id: currentSite?.account?.id,
            organization_id: currentSite?.organization?.id,
            site_id: currentSite?.id,
            state_event_was_triggered_on: 'Recordings List',
            user_id: user?.id,
          });
        }}
      >
        {scoreInstance}
      </ScoreContainer>
    </BasePopover>
  );
};

export const ScoreCell = memo(UnMemoScoreCell);
